import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  TextField,
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DndContext, closestCenter, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { debounce } from 'lodash';
import { translate as t } from 'hooks/useTranslations';
import { FieldOption } from '../../FieldBank/_types';
import { success } from '_lex/_foundations/phxColors';

export interface SingleSelectOptionManagerProps {
  options: FieldOption[];
  onChange: (options: FieldOption[]) => void;
  mode: 'fieldBank' | 'templateBuilder';
  errors?: any;
  isDraft?: boolean;
  trackUserActivity?: () => void;
  validationError?: string;
}

interface SortableOptionItemProps {
  option: FieldOption;
  index: number;
  onOptionChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
  onToggleVisibility?: (index: number) => void;
  mode: 'fieldBank' | 'templateBuilder';
  canRemove: boolean;
  canEditLabel: boolean;
  trackUserActivity?: () => void;
}

const SortableOptionItem: React.FC<SortableOptionItemProps> = ({
  option,
  index,
  onOptionChange,
  onRemove,
  onToggleVisibility,
  mode,
  canRemove,
  canEditLabel,
  trackUserActivity,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `option-${index}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Store the callback in a ref to avoid recreating debounced function
  const onOptionChangeRef = useRef(onOptionChange);
  onOptionChangeRef.current = onOptionChange;

  // Create a stable debounced function with maxWait to prevent indefinite delays
  const debouncedOnOptionChange = useMemo(
    () =>
      debounce(
        (idx: number, value: string) => {
          onOptionChangeRef.current(idx, value);
        },
        300, // Reduced delay for better responsiveness
        { maxWait: 1000 } // Ensure it fires within 1 second max
      ),
    [index] // Only recreate if index changes (when items are reordered)
  );

  // Local state for immediate UI feedback
  const [localValue, setLocalValue] = useState(option.label || '');

  // Update local value when option prop changes
  useEffect(() => {
    setLocalValue(option.label || '');
  }, [option.label]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedOnOptionChange.cancel();
    };
  }, [debouncedOnOptionChange]);

  const handleLabelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    // Track user activity if available
    if (trackUserActivity) {
      trackUserActivity();
    }

    // Update local state immediately for responsive UI
    setLocalValue(newValue);

    // If the value is empty or whitespace only, trigger immediately to avoid delays
    // when users are clearing fields
    if (!newValue.trim()) {
      debouncedOnOptionChange.cancel();
      onOptionChangeRef.current(index, newValue);
    } else {
      // Debounce the actual onChange call for non-empty values
      debouncedOnOptionChange(index, newValue);
    }
  };

  const handleRemove = () => {
    onRemove(index);
  };

  const handleToggleVisibility = () => {
    onToggleVisibility?.(index);
  };

  // Determine if option is inherited (has fieldOptionId) or custom (no fieldOptionId)
  const isInherited = Boolean(option.fieldOptionId);
  const isCustom = !isInherited;

  // For inherited options, use isVisible property; for custom options, always visible
  const isVisible = isCustom ? true : option.isVisible !== false;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        opacity: isVisible ? 1 : 0.6,
      }}
    >
      <IconButton
        {...{ ...attributes, ...listeners }}
        size='small'
        sx={{ cursor: 'grab' }}
      >
        <DragIndicatorIcon />
      </IconButton>

      {/* Option Label Input */}
      <TextField
        value={localValue}
        onChange={handleLabelChange}
        variant='outlined'
        size='small'
        fullWidth
        placeholder={mode === 'templateBuilder' ? `New Option` : `Option ${index + 1}`}
        disabled={!canEditLabel || (mode === 'templateBuilder' && isInherited)}
        sx={{
          flex: 1,
          paddingTop: 0,
          p: 2,
          ...((!canEditLabel || (mode === 'templateBuilder' && isInherited)) && {
            '& .MuiInputBase-root': {
              backgroundColor: 'grey.100',
            },
            '& .MuiInputBase-input': {
              color: 'text.disabled',
            },
          }),
        }}
      />

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {mode === 'templateBuilder' && isInherited ? (
          // For inherited options in template builder, show visibility toggle
          <Tooltip
            title={isVisible ? 'Hide option' : 'Show option'}
            placement='top'
          >
            <Switch
              checked={isVisible}
              onChange={handleToggleVisibility}
              size='small'
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: success[350], // Green color using phxColors
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: success[350], // Green track when enabled
                },
              }}
            />
          </Tooltip>
        ) : (
          // For field bank or custom template options, show delete button
          <IconButton
            onClick={handleRemove}
            size='small'
            color='error'
            disabled={!canRemove}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

const SingleSelectOptionManager: React.FC<SingleSelectOptionManagerProps> = ({
  options,
  onChange,
  mode,
  errors,
  isDraft = true,
  trackUserActivity,
  validationError,
}) => {
  const [fieldOptions, setFieldOptions] = useState<FieldOption[]>(options);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Update local state when props change
  useEffect(() => {
    setFieldOptions(options);
  }, [options]);

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = fieldOptions.map((opt, i) => (i === index ? { ...opt, label: value } : opt));
    setFieldOptions(newOptions);
    onChange(newOptions);
  };

  const handleAddOption = () => {
    const newOptions = [
      ...fieldOptions,
      {
        id: null, // New options get id: null
        fieldOptionId: null, // Custom options have no fieldOptionId
        label: '',
        status: 'Active' as 'Active',
        isVisible: true, // Custom options are always visible
      },
    ];
    setFieldOptions(newOptions);
    onChange(newOptions);
  };

  const handleRemoveOption = (index: number) => {
    let updatedOptions = fieldOptions.filter((_, i) => i !== index);

    // If removing would result in fewer than 2 options, add a new empty option
    if (updatedOptions.length < 2) {
      updatedOptions.push({
        id: null, // New options get id: null
        fieldOptionId: null, // Custom options have no fieldOptionId
        label: '',
        status: 'Active' as 'Active',
        isVisible: true, // Custom options are always visible
      });
    }

    setFieldOptions(updatedOptions);
    onChange(updatedOptions);
  };

  const handleToggleVisibility = (index: number) => {
    const newOptions = fieldOptions.map((opt, i) =>
      i === index
        ? {
            ...opt,
            isVisible: !(opt.isVisible !== false), // Toggle isVisible property
          }
        : opt
    );
    setFieldOptions(newOptions);
    onChange(newOptions);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const activeIndex = fieldOptions.findIndex((_, i) => `option-${i}` === active.id);
      const overIndex = fieldOptions.findIndex((_, i) => `option-${i}` === over.id);

      if (activeIndex !== -1 && overIndex !== -1) {
        const newOptions = arrayMove(fieldOptions, activeIndex, overIndex);
        setFieldOptions(newOptions);
        onChange(newOptions);
      }
    }
  };

  const sortableIds = fieldOptions
    .map((option, index) => (option.status === 'Active' ? `option-${index}` : null))
    .filter((id) => id !== null) as string[];

  return (
    <Box>
      <Typography
        variant='subtitle2'
        sx={{ mb: 2, fontWeight: 600 }}
      >
        {mode === 'fieldBank' ? t('performanceReporting.fieldBank.form.selectOptions') : 'Options'}
      </Typography>

      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <SortableContext
          items={sortableIds}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing={1}>
            {fieldOptions.map((option, originalIndex) => {
              // Only render active options
              if (option.status !== 'Active') {
                return null;
              }

              const isPersisted = Boolean(option.id && option.id > 0);
              const isInherited = Boolean(option.fieldOptionId); // Has fieldOptionId = inherited
              const isCustom = !isInherited; // No fieldOptionId = custom
              const canEditLabel = mode === 'templateBuilder' ? isCustom : isDraft || !isPersisted;
              const canRemove = mode === 'fieldBank' ? true : isCustom;

              return (
                <SortableOptionItem
                  key={`option-${originalIndex}`}
                  option={option}
                  index={originalIndex}
                  onOptionChange={handleOptionChange}
                  onRemove={handleRemoveOption}
                  onToggleVisibility={handleToggleVisibility}
                  mode={mode}
                  canRemove={canRemove}
                  canEditLabel={canEditLabel}
                  trackUserActivity={trackUserActivity}
                />
              );
            })}
          </Stack>
        </SortableContext>
      </DndContext>

      <Button
        variant='text'
        startIcon={<AddIcon />}
        onClick={handleAddOption}
        sx={{
          mt: 2,
          color: 'primary.main',
          fontSize: mode === 'templateBuilder' ? '0.875rem' : undefined,
          '&:hover': {
            bgcolor: 'primary.50',
          },
        }}
      >
        {mode === 'fieldBank' ? t('performanceReporting.fieldBank.form.addOption') : 'Add option'}
      </Button>

      {(errors?.fieldOptions || validationError) && (
        <Typography
          color='error'
          variant='caption'
          sx={{ mt: 1, display: 'block' }}
        >
          {validationError || (errors.fieldOptions.message as string)}
        </Typography>
      )}
    </Box>
  );
};

export default SingleSelectOptionManager;
